/* eslint-disable @typescript-eslint/no-empty-function */
/**
 * テナント管理 API hooks
 * Python Backend (PostgreSQL) 用
 */
import { useCallback, useEffect, useState } from "react";
import { restClient } from "../lib/rest-client";

// =====================================
// Types
// =====================================

export interface Tenant {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  role: "owner" | "admin" | "member";
}

export interface CreateTenantInput {
  name: string;
  description?: string;
}

// =====================================
// useCreateTenant - テナント作成
// =====================================

export interface UseCreateTenantResult {
  createTenant: (input: CreateTenantInput) => Promise<Tenant>;
  loading: boolean;
  error: Error | undefined;
}

/**
 * テナントを作成するhook
 * @return {UseCreateTenantResult} 作成関数、ローディング状態、エラー
 */
export function useCreateTenant(): UseCreateTenantResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const createTenant = useCallback(
    async (input: CreateTenantInput): Promise<Tenant> => {
      setLoading(true);
      setError(undefined);

      try {
        const tenant = await restClient.post<Tenant>("/tenants", input);
        return tenant;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    createTenant,
    loading,
    error,
  };
}

// =====================================
// Tenant Detail Types
// =====================================

/** テナントメンバー情報 */
export interface TenantMember {
  userId: string;
  email: string;
  familyName: string;
  givenName: string;
  role: string;
  crmRole: string | null;
}

/** テナント詳細レスポンス */
export interface TenantDetail {
  id: string;
  name: string;
  description: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  licenses: string[];
  members: TenantMember[];
}

/** テナント更新リクエスト */
export interface UpdateTenantInput {
  name?: string;
  description?: string;
}

// =====================================
// useTenantDetailApi - テナント詳細取得
// =====================================

export interface UseTenantDetailApiResult {
  data: TenantDetail | undefined;
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<TenantDetail>;
}

/**
 * テナント詳細を取得するhook (owner/admin only)
 * @param {string} tenantId テナントID
 * @return {UseTenantDetailApiResult} テナント詳細取得結果
 */
export function useTenantDetailApi(tenantId: string): UseTenantDetailApiResult {
  const [data, setData] = useState<TenantDetail | undefined>(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  const fetchTenant = useCallback(async (): Promise<TenantDetail> => {
    setLoading(true);
    setError(undefined);

    try {
      const responseData = await restClient.get<TenantDetail>(
        `/tenants/${tenantId}`,
      );
      setData(responseData);
      return responseData;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    if (tenantId) {
      fetchTenant().catch(() => {});
    }
  }, [tenantId, fetchTenant]);

  return {
    data,
    error,
    loading,
    refetch: fetchTenant,
  };
}

// =====================================
// useUpdateTenantApi - テナント更新
// =====================================

export interface UseUpdateTenantApiResult {
  updateTenant: (
    tenantId: string,
    input: UpdateTenantInput,
  ) => Promise<TenantDetail>;
  loading: boolean;
  error: Error | undefined;
}

/**
 * テナントを更新するhook (owner/admin only)
 * @return {UseUpdateTenantApiResult} テナント更新機能
 */
export function useUpdateTenantApi(): UseUpdateTenantApiResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const updateTenant = useCallback(
    async (
      tenantId: string,
      input: UpdateTenantInput,
    ): Promise<TenantDetail> => {
      setLoading(true);
      setError(undefined);

      try {
        const responseData = await restClient.put<TenantDetail>(
          `/tenants/${tenantId}`,
          input,
        );
        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    updateTenant,
    loading,
    error,
  };
}

// =====================================
// useChangeMemberRole - メンバーロール変更
// =====================================

/** useChangeMemberRoleの戻り値 */
export type UseChangeMemberRoleResult = {
  changeMemberRole: (
    tenantId: string,
    userId: string,
    role: string,
  ) => Promise<TenantMember>;
  loading: boolean;
  error: Error | undefined;
};

/**
 * テナントメンバーのロールを変更するhook (owner/admin only)
 * @return {UseChangeMemberRoleResult} ロール変更機能
 */
export function useChangeMemberRole(): UseChangeMemberRoleResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const changeMemberRole = useCallback(
    async (
      tenantId: string,
      userId: string,
      role: string,
    ): Promise<TenantMember> => {
      setLoading(true);
      setError(undefined);

      try {
        const responseData = await restClient.put<TenantMember>(
          `/tenants/${tenantId}/members/${userId}/role`,
          { role },
        );
        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    changeMemberRole,
    loading,
    error,
  };
}

// =====================================
// useChangeCrmRole - CRMロール変更
// =====================================

/** useChangeCrmRoleの戻り値 */
export type UseChangeCrmRoleResult = {
  changeCrmRole: (
    tenantId: string,
    userId: string,
    crmRole: string | null,
  ) => Promise<TenantMember>;
  loading: boolean;
  error: Error | undefined;
};

/**
 * テナントメンバーのCRMロールを変更するhook (owner/admin only)
 * @return {UseChangeCrmRoleResult} CRMロール変更機能
 */
export function useChangeCrmRole(): UseChangeCrmRoleResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const changeCrmRole = useCallback(
    async (
      tenantId: string,
      userId: string,
      crmRole: string | null,
    ): Promise<TenantMember> => {
      setLoading(true);
      setError(undefined);

      try {
        const responseData = await restClient.patch<TenantMember>(
          `/tenants/${tenantId}/members/${userId}/crm-role`,
          { crmRole },
        );
        return responseData;
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    changeCrmRole,
    loading,
    error,
  };
}
